import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import ADLRecordList from "./Phase-05/ADLsRecordSheet/ADLRecordList";
import { selectHasPermission } from "../store/slices/authSlice";
import { 
    fetchAdlRecords, 
    deleteAdlRecord,
    clearError 
} from "../store/slices/adlRecordsSlice";

const ADLRecordSheets = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // ✅ Redux state
    const { 
        adlRecords, 
        isLoading, 
        error 
    } = useSelector((state) => state.adlRecords);

    // ✅ Permissions (you can later bind to roles)
    const canManage = useSelector((state) =>
        selectHasPermission(state, "adl-record-manage")
    );
    const canCreate = useSelector((state) =>
        selectHasPermission(state, "adl-record-create")
    );
    const canEdit = useSelector((state) =>
        selectHasPermission(state, "adl-record-edit")
    );
    const canDelete = useSelector((state) =>
        selectHasPermission(state, "adl-record-delete")
    );

    // ✅ State for local UI
    const [localLoading, setLocalLoading] = useState(false);

    useEffect(() => {
        // Fetch ADL records on component mount
        dispatch(fetchAdlRecords());
    }, [dispatch]);

    // ✅ Handle loading state (combine Redux loading and local loading)
    useEffect(() => {
        if (isLoading) {
            setLocalLoading(true);
        } else {
            // Small delay for smooth transition
            const timer = setTimeout(() => setLocalLoading(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    // ✅ Clear errors when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearError());
        };
    }, [dispatch]);

    // ✅ Handlers
    const handleAddRecord = () => navigate("/phase05/adl-record/add");
    
    const handleEditRecord = (id) => navigate(`/phase05/adl-record/edit/${id}`);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This ADL record will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteAdlRecord(id)).unwrap();
                Swal.fire("Deleted!", "The record has been removed.", "success");
            } catch (error) {
                Swal.fire("Error!", "Failed to delete the record.", "error");
            }
        }
    };

    // ✅ Transform data for the table
    const transformRecordsForTable = () => {
        return adlRecords.map(record => {
            // Handle both response structures: record.record or direct record
            const recordData = record.record || record;
            
            return {
                id: recordData.id,
                client_name: recordData.client_name || 'N/A',
                room_number: recordData.room_number || 'N/A',
                date_range: recordData.date_range || 'N/A',
                checked_by: recordData.checked_by || 'N/A',
                admission_date: recordData.created_at ? new Date(recordData.created_at).toLocaleDateString() : 'N/A',
                activities_count: record.activityRecords ? record.activityRecords.length : 0,
                // Include the full record for potential detail views
                fullRecord: record
            };
        });
    };

    if (!canManage) {
        return (
            <Navigate
                to="/dashboard"
                state={{ error: "You do not have permission to manage ADL Records." }}
                replace
            />
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                {error && (
                    <div
                        className="alert alert-danger alert-dismissible fade show"
                        role="alert"
                    >
                        <strong>Error:</strong> {error}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => dispatch(clearError())}
                        ></button>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        {localLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <ADLRecordList
                                records={transformRecordsForTable()}
                                onAddRecord={canCreate ? handleAddRecord : null}
                                onEditRecord={canEdit ? handleEditRecord : null}
                                onDelete={canDelete ? handleDelete : null}
                                isLoading={localLoading}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ADLRecordSheets;